import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Empty, Form, Pagination, Select, Space, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { getMemberEventTypesApi, getMemberJourneyApi } from '@/api/member/member';
import styles from '../../index.module.scss';

const { RangePicker } = DatePicker;

type Props = {
  oneId: string;
  shopId?: number | null;
  active: boolean;
};

const MemberJourneyTab: React.FC<Props> = ({ oneId, shopId, active }) => {
  const [form] = Form.useForm();
  const [eventTypes, setEventTypes] = useState<any[]>([]);
  const [journeyList, setJourneyList] = useState<any[]>([]);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    form.resetFields();
    setEventTypes([]);
    setJourneyList([]);
    setPageNum(1);
    setPageSize(10);
    setTotal(0);
    setInitialized(false);
  }, [oneId, shopId, form]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!active || !oneId || !shopId || initialized) return;
    fetchEventTypes();
    handleSearch(1);
    setInitialized(true);
  }, [active, initialized, oneId, shopId]);

  const fetchEventTypes = async () => {
    try {
      const res = await getMemberEventTypesApi();
      if (res?.code === 200 || res?.code === 0) {
        setEventTypes(res.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchJourney = async (override?: Partial<{ pageNum: number; pageSize: number }>) => {
    if (!oneId || !shopId) return;
    const values = form.getFieldsValue();
    const timeRange: Dayjs[] | undefined = values.timeRange;
    const params = {
      oneId,
      shopId,
      startTime: timeRange?.[0] ? timeRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      endTime: timeRange?.[1] ? timeRange[1].endOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      eventType: values.eventType || undefined,
      pageNum: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
      isQueryOrder: true,
    } as Record<string, any>;
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === '') {
        delete params[key];
      }
    });
    setJourneyLoading(true);
    try {
      const res = await getMemberJourneyApi(params);
      if (res?.code === 200) {
        setJourneyList(res.rows || []);
        setTotal(res.total || 0);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setJourneyLoading(false);
    }
  };

  const handleSearch = (resetPage?: number) => {
    fetchJourney({ pageNum: resetPage ?? 1 });
  };

  const handleReset = () => {
    form.resetFields();
    handleSearch(1);
  };

  const handlePageChange = (page: number, size?: number) => {
    fetchJourney({ pageNum: page, pageSize: size });
  };

  const timelineContent = useMemo(() => {
    if (!journeyList.length) {
      return <Empty description="暂无履历" />;
    }
    const nodes: React.ReactNode[] = [];
    let prevYear = '';
    journeyList.forEach((item, index) => {
      const eventTime = item.eventTime ? dayjs(item.eventTime) : null;
      const year = eventTime ? eventTime.format('YYYY') : '未知';
      if (year !== prevYear) {
        prevYear = year;
        nodes.push(
          <div
            key={`year-${year}-${index}`}
            className={styles['journey-year-tag']}>
            {year}年
          </div>,
        );
      }
      nodes.push(
        <div
          key={`node-${index}`}
          className={styles['journey-node']}>
          <div className={styles['journey-node__indicator']}>
            <span className={styles['journey-node__dot']} />
            <span className={styles['journey-node__line']} />
          </div>
          <div className={styles['journey-node__body']}>
            <div className={styles['journey-node__time']}>
              <div>{eventTime ? eventTime.format('YYYY-MM-DD') : '-'}</div>
              <div>{eventTime ? eventTime.format('HH:mm:ss') : ''}</div>
            </div>
            <div className={styles['journey-node__title']}>{item.eventType || '事件'}</div>
            <div className={styles['journey-node__meta']}>
              {item.channelName && <span>渠道：{item.channelName}</span>}
              {item.shopName && <span>店铺：{item.shopName}</span>}
              {item.orderId && <span>订单号：{item.orderId}</span>}
              {item.orderPrice && <span>订单金额：{item.orderPrice}</span>}
              {item.orderStatus && <span>订单状态：{item.orderStatus}</span>}
            </div>
            <div className={styles['journey-node__meta']}>
              {item.memberCardName && <span>会员卡：{item.memberCardName}</span>}
              {item.changeType && <span>变更类型：{item.changeType}</span>}
              {item.beforeValue && <span>变更前：{item.beforeValue}</span>}
              {item.addPoint && <span>变更值：{item.addPoint}</span>}
              {item.afterValue && <span>变更后：{item.afterValue}</span>}
            </div>
            {item.changeCause && <div className={styles['journey-node__remark']}>原因：{item.changeCause}</div>}
            {item.remark && <div className={styles['journey-node__remark']}>备注：{item.remark}</div>}
          </div>
        </div>,
      );
    });
    return <div className={styles['journey-timeline']}>{nodes}</div>;
  }, [journeyList]);

  const renderJourneyTimeline = () => {
    return timelineContent;
  };

  return (
    <div className={styles['journey-section']}>
      <Form
        form={form}
        layout="inline"
        className={styles['journey-filter']}>
        <Form.Item
          label="时间范围"
          name="timeRange">
          <RangePicker />
        </Form.Item>
        <Form.Item
          label="事件类型"
          name="eventType">
          <Select
            placeholder="全部"
            allowClear
            style={{ width: 160 }}
            options={eventTypes.map(item => ({ label: item.eventDescription, value: item.eventType }))}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(1)}>
              查询
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>
      <Spin spinning={journeyLoading}>{renderJourneyTimeline()}</Spin>
      <div className={styles['journey-pagination']}>
        <Pagination
          current={pageNum}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          showTotal={t => `共 ${t} 条`}
          onChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default MemberJourneyTab;
